import { useState } from "react";
import { Send, CalendarClock, Pencil, SkipForward, RotateCcw, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { tr } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import CaptionText from "./CaptionText";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "");

function Posters({ s }) {
  const urls = (s.images || []).flatMap((set) => set.urls.map((u) => ({ u, lang: set.lang })));
  return (
    <div className="flex gap-2 overflow-x-auto snap-x snap-mandatory pb-1" data-testid={`sg-posters-${s.id}`}>
      {urls.map(({ u, lang }, i) => (
        <a key={u} href={u} target="_blank" rel="noopener noreferrer" className={`snap-start shrink-0 ${urls.length > 1 ? "w-[85%]" : "w-full"}`}>
          <img src={u} alt={`${s.car} ${lang}`} loading="lazy" className="w-full rounded-lg border border-[var(--line)] bg-black" data-testid={`sg-poster-img-${s.id}-${i}`} />
        </a>
      ))}
    </div>
  );
}

function LangPicker({ s, busy, onPick }) {
  const opt = (lang, mode) => {
    const on = s.lang === lang && (lang !== "both" || s.both_mode === mode);
    return { on, click: () => !on && onPick({ lang, both_mode: mode || "one" }) };
  };
  const btn = (key, o, label) => (
    <button key={key} type="button" disabled={busy} onClick={o.click} data-testid={`sg-lang-${key}-${s.id}`}
      className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${o.on ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "border-[var(--line)] hover:bg-[var(--gray-100)]"}`}>{label}</button>
  );
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`sg-lang-picker-${s.id}`}>
      {btn("en", opt("en"), tr("sg.lang_en"))}
      {btn("ar", opt("ar"), tr("sg.lang_ar"))}
      {btn("both-one", opt("both", "one"), tr("sg.one_poster"))}
      {btn("both-two", opt("both", "two"), tr("sg.two_posters"))}
    </div>
  );
}

function StatusLine({ s }) {
  if (s.status === "scheduled") return <span data-testid={`sg-when-${s.id}`}>{tr("sg.scheduled_for", { p0: fmt(s.scheduled_at) })}</span>;
  if (s.status === "posted") return <span data-testid={`sg-when-${s.id}`}>{tr("sg.posted_mock")} · {fmt(s.posted_at)}</span>;
  if (s.status === "skipped") return <span data-testid={`sg-when-${s.id}`}>{s.skipped_by ? tr("sg.skipped_by", { p0: s.skipped_by }) : tr("sg.auto_skipped")}</span>;
  return null;
}

function Actions({ s, busy, act, onEdit, onSchedule }) {
  const { can } = useAuth();
  const canPost = can("social", "approve");
  const canEdit = can("social", "create");
  if (s.status === "pending") return (
    <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
      {canPost && <button className="btn-primary justify-center" disabled={busy} onClick={() => act("post", tr("sg.posted_toast"))} data-testid={`sg-post-${s.id}`}><Send size={14} /> {tr("sg.yes")}</button>}
      {canPost && <button className="btn-secondary justify-center" disabled={busy} onClick={onSchedule} data-testid={`sg-schedule-${s.id}`}><CalendarClock size={14} /> {tr("sg.schedule")}</button>}
      {canEdit && <button className="btn-secondary justify-center" disabled={busy} onClick={onEdit} data-testid={`sg-edit-${s.id}`}><Pencil size={14} /> {tr("sg.edit")}</button>}
      {canEdit && <button className="btn-secondary justify-center" disabled={busy} onClick={() => act("skip", tr("sg.skipped_toast"))} data-testid={`sg-skip-${s.id}`}><SkipForward size={14} /> {tr("sg.skip")}</button>}
    </div>
  );
  if (canEdit && (s.status === "skipped" || s.status === "scheduled")) return (
    <button className="btn-secondary" disabled={busy} onClick={() => act("restore", tr("sg.restored_toast"))} data-testid={`sg-restore-${s.id}`}>
      <RotateCcw size={14} /> {s.status === "skipped" ? tr("sg.restore") : tr("sg.unschedule")}
    </button>
  );
  return null;
}

export default function SuggestionCard({ s, onChanged, onEdit, onSchedule }) {
  const { can } = useAuth();
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try { const { data } = await fn(); onChanged(data); if (msg) toast.success(msg); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const act = (what, msg) => run(() => api.post(`/social/suggestions/${s.id}/${what}`), msg);
  const pickLang = (body) => run(() => api.post(`/social/suggestions/${s.id}/regenerate`, body), tr("sg.regenerated_toast"));
  const ok = (s.number_check || "ok") === "ok";
  return (
    <article className="panel p-4 grid grid-cols-1 md:grid-cols-[minmax(0,320px)_1fr] gap-4 relative" data-testid={`sg-card-${s.id}`}>
      {busy && <div className="absolute inset-0 bg-white/60 grid place-items-center z-10 rounded-[inherit]" data-testid={`sg-busy-${s.id}`}><Loader2 className="animate-spin" /></div>}
      <Posters s={s} />
      <div className="min-w-0 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-semibold text-base" data-testid={`sg-car-${s.id}`}>{s.car}</h3>
          <bdi className="text-xs text-[var(--gray-400)]">{s.stock_number}</bdi>
          {(s.reasons || []).map((r) => <span key={r} className="text-[11px] px-2 py-0.5 rounded-full bg-[var(--gray-100)]" data-testid={`sg-reason-${r}-${s.id}`}>{tr(`sg.reason_${r /* i18n-keys: new_car,new_photos,price_change,available */}`)}</span>)}
        </div>
        <div className="text-xs text-[var(--gray-400)] flex flex-wrap gap-x-3 gap-y-1">
          <span>{s.ai_background ? tr("sg.ai_bg") : tr("sg.original_bg")}</span>
          <span className={`inline-flex items-center gap-1 ${ok ? "text-emerald-600" : "text-amber-600"}`} data-testid={`sg-number-check-${s.id}`}>
            {ok ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />} {ok ? tr("sg.number_check_ok") : tr("sg.number_check_bad", { p0: s.number_check })}
          </span>
          <StatusLine s={s} />
        </div>
        {s.status === "pending" && can("social", "create") && <LangPicker s={s} busy={busy} onPick={pickLang} />}
        <div>
          <div className="eyebrow mb-1">{tr("sg.caption")}</div>
          <CaptionText text={s.caption} testId={`sg-caption-${s.id}`} className="max-h-56 overflow-y-auto border border-[var(--line)] rounded-lg p-3 bg-[var(--gray-50,#fafafa)]" />
        </div>
        {s.status === "pending" && (can("social", "approve") || can("social", "create")) && <p className="font-semibold" data-testid={`sg-question-${s.id}`}>{tr("sg.question")}</p>}
        <Actions s={s} busy={busy} act={act} onEdit={() => onEdit(s)} onSchedule={() => onSchedule(s)} />
      </div>
    </article>
  );
}
